import React, { useEffect, useState } from 'react'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material'
import GuidanceNote from '../../components/GuidanceNote'
import GoogleIcon from '../../components/icon/GoogleIcon'
import {
  UI_COLOR_DANGER_NEGATIVE,
  UI_COLOR_PRIMARY_ALT,
  UI_COLOR_SUCCESS,
  UI_COLOR_TEXT_BODY_DARK,
  UI_COLOR_TEXT_DISABLED_FLAT,
  UI_COLOR_TEXT_SUMMARY_LABEL
} from '../../constants/uiColors'
import { UI_TEXT_EMPTY_VALUE } from '../../constants/uiText'
import { RbacRoleChip } from '../components/RbacChips'
import RbacUserDetailToolbar from '../components/RbacUserDetailToolbar'
import RbacUserSummaryCard from '../components/RbacUserSummaryCard'
import RbacDetailSection from '../components/RbacDetailSection'
import {
  rbacDetailsCardGridSx,
  rbacDetailsFieldSx,
  rbacSectionIconBoxSx
} from '../components/constants/rbacDetailCardConstants'
import {
  RBAC_USER_DETAIL_TEXTS,
  rbacPendingNoteColors,
  rbacPendingNoteIconSx,
  rbacPendingNoteMessageSx,
  rbacPendingNoteSx,
  rbacPendingNoteTitleSx,
  rbacResendButtonSx
} from './shared/rbacUserDetailConstants'
import {
  buildRbacUserNameFields,
  formatRbacInvitationResendCountdown,
  formatRbacUserCreatedBy,
  formatRbacUserDate,
  getRbacInvitationResendRemainingMs,
  isRbacUserPending
} from './shared/rbacUserDetailHelpers'

const RESEND_COOLDOWN_TICK_MS = 30000

function RoleValue({ roles = [] }) {
  if (!roles.length) return <RbacRoleChip label="SIN ROL" unassigned />

  return (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {roles.map((role) => (
        <RbacRoleChip key={role.id} label={role.name} />
      ))}
    </Box>
  )
}

function SessionsValue({ user }) {
  if (isRbacUserPending(user)) {
    return (
      <Typography
        component="span"
        sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
      >
        Aún no ha iniciado sesión.
      </Typography>
    )
  }

  const activeSessions = Number(user?.active_sessions_count ?? 0)
  const hasActiveSessions = activeSessions > 0

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
      <Typography
        component="span"
        sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
      >
        {formatRbacUserDate(user?.last_session_at)}
      </Typography>
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: hasActiveSessions ? UI_COLOR_SUCCESS : UI_COLOR_DANGER_NEGATIVE,
          flexShrink: 0
        }}
      />
      <Typography
        component="span"
        sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
      >
        {`${activeSessions} sesione(s)`}
      </Typography>
    </Box>
  )
}

function DetailFieldMui({ label, value }) {
  return (
    <Box sx={rbacDetailsFieldSx}>
      <Typography
        sx={{
          color: UI_COLOR_TEXT_SUMMARY_LABEL,
          fontSize: 13,
          fontWeight: 800,
          lineHeight: 1.2,
          textTransform: 'uppercase'
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          color: UI_COLOR_TEXT_BODY_DARK,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.4,
          mt: 0.5,
          wordBreak: 'break-word'
        }}
      >
        {value || UI_TEXT_EMPTY_VALUE}
      </Typography>
    </Box>
  )
}

function DetailsSummarySectionMui({ summaryTitle, summaryIcon, summaryFields = [] }) {
  const visibleFields = summaryFields.filter((field) => !field.hidden)

  if (!visibleFields.length) return null

  return (
    <RbacDetailSection title={summaryTitle} icon={summaryIcon} contentSx={rbacDetailsCardGridSx}>
      {visibleFields.map((field, index) => (
        <DetailFieldMui key={`${field.label}-${index}`} label={field.label} value={field.value} />
      ))}
    </RbacDetailSection>
  )
}

export default function RbacUserDetailTab({
  user,
  canManage,
  resending,
  onEditUser,
  onStatusAction,
  onResendInvitation
}) {
  const [now, setNow] = useState(() => Date.now())
  const remainingMs = getRbacInvitationResendRemainingMs(user, now)
  const onCooldown = remainingMs > 0
  const resendDisabled = resending || onCooldown
  const cooldownTooltip =
    onCooldown && !resending
      ? RBAC_USER_DETAIL_TEXTS.resendCooldownTooltip.replace(
          '{time}',
          formatRbacInvitationResendCountdown(remainingMs)
        )
      : ''

  useEffect(() => {
    setNow(Date.now())
  }, [user?.invitation_resend_available_at])

  useEffect(() => {
    if (!onCooldown) return undefined

    const timer = window.setInterval(() => setNow(Date.now()), RESEND_COOLDOWN_TICK_MS)
    return () => window.clearInterval(timer)
  }, [onCooldown, user?.invitation_resend_available_at])

  const fields = [
    ...buildRbacUserNameFields(user),
    { label: 'Correo electrónico', value: user?.email },
    { label: 'Rol asignado', value: <RoleValue roles={user?.roles} /> },
    { label: 'Número de teléfono', value: user?.phone },
    { label: 'Creado por', value: formatRbacUserCreatedBy(user) },
    { label: 'Sesiones activas', value: <SessionsValue user={user} /> }
  ]

  return (
    <div className="container-fluid px-0">
      <RbacUserDetailToolbar
        user={user}
        canManage={canManage}
        onEditUser={onEditUser}
        onStatusAction={onStatusAction}
      />

      <RbacUserSummaryCard user={user} />

      {isRbacUserPending(user) && (
        <GuidanceNote
          title={RBAC_USER_DETAIL_TEXTS.pendingTitle}
          message={RBAC_USER_DETAIL_TEXTS.pendingMessage}
          icon={<WarningAmberOutlinedIcon />}
          singleLine
          accentColor={rbacPendingNoteColors.accentColor}
          backgroundColor={rbacPendingNoteColors.backgroundColor}
          sx={rbacPendingNoteSx}
          headerSx={{ width: '100%' }}
          iconSx={rbacPendingNoteIconSx}
          titleSx={rbacPendingNoteTitleSx}
          messageSx={rbacPendingNoteMessageSx}
        >
          {canManage && (
            <Tooltip
              title={cooldownTooltip}
              arrow
              placement="bottom"
              disableHoverListener={!cooldownTooltip}
            >
              <Box
                component="span"
                sx={{
                  ml: { xs: 0, md: 'auto' },
                  display: 'inline-flex',
                  cursor: onCooldown ? 'not-allowed' : 'inherit'
                }}
              >
                {resendDisabled ? (
                  <Typography
                    component="span"
                    sx={{
                      color: UI_COLOR_TEXT_DISABLED_FLAT,
                      fontSize: 14,
                      fontWeight: 700,
                      lineHeight: 1.75,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {resending
                      ? RBAC_USER_DETAIL_TEXTS.resendingEmail
                      : RBAC_USER_DETAIL_TEXTS.resendEmail}
                  </Typography>
                ) : (
                  <Button size="small" onClick={onResendInvitation} sx={rbacResendButtonSx}>
                    {RBAC_USER_DETAIL_TEXTS.resendEmail}
                  </Button>
                )}
              </Box>
            </Tooltip>
          )}
        </GuidanceNote>
      )}

      <DetailsSummarySectionMui
        summaryFields={fields}
        summaryTitle={RBAC_USER_DETAIL_TEXTS.summaryTitle}
        summaryIcon={
          <Box sx={rbacSectionIconBoxSx}>
            <GoogleIcon name="info" size={16} color={UI_COLOR_PRIMARY_ALT} />
          </Box>
        }
      />
    </div>
  )
}
